import {
    checkBoxLabelChecked,
    checkBoxLabelUnchecked,
} from '@dat/iq-style-processor';
import { startCase } from 'lodash';
import { InfoLabel, InfoLabelSchema } from '../../models';

type Components =
    | 'BenchmarkAnalytics'
    | 'RateLane'
    | 'Trendlines'
    | 'NX'
    | 'Builder'
    | 'Trendlines'
    | 'MarketConditions'
    | 'Landing'
    | 'RateNational'
    | 'Job'
    | 'RateLaneBatch'
    | 'Unknown'
    | 'Social'
    | 'Core'
    | 'RFP'
    | 'SharedComponents'
    | 'CI_CD';
const ComponentAbbreviations: Record<Components, string> = {
    BenchmarkAnalytics: 'BA',
    Builder: 'BUILD',
    RateLane: 'RL',
    MarketConditions: 'MC',
    RateNational: 'RN',
    RFP: 'RFP',
    Job: 'JOB',
    RateLaneBatch: 'BATCH',
    Social: 'SOCIAL',
    Core: 'CORE',
    SharedComponents: 'UI',
    CI_CD: 'CI',
    Trendlines: 'TL',
    Unknown: '',
    NX: 'NX',
    Landing: 'LAND',
};

type EnvironmentKeys =
    | 'nprod'
    | 'staging'
    | 'prod'
    | 'notUsed'
    | 'Unknown';
const EnvironmentMap: Record<EnvironmentKeys, string> = {
    nprod: 'nprod',
    staging: 'staging',
    prod: 'prod',
    notUsed: 'notUsed',
    Unknown: 'unknown',
};

type Teams =
    | 'RedBarclays'
    | 'PorkChopExpress'
    | 'BinaryBrains'
    | 'FrontEndSquad'
    | 'Unknown'
    | 'Elevation'
    | 'CrossTeam'
    | 'WorksOnMyMachine';
const TeamQuery: Record<Teams, string> = {
    RedBarclays: 'Red Barclays',
    PorkChopExpress: 'Pork Chop Express',
    BinaryBrains: 'Binary Brains',
    Elevation: 'Elevation',
    CrossTeam: 'IQ Cross Team',
    FrontEndSquad: 'Front End Squad',
    WorksOnMyMachine: 'Works On My Machine',
    Unknown: '',
};

export function getProjectIcon(type: string): InfoLabel {
    return {
        Feature: {
            label: `<span class="material-symbols-outlined text-[24px]">
home_repair_service
</span>`,
        },
    }[type];
}

export function getProjectTitle(name: string): InfoLabel {
    return {
        label: `<span class="font-card-title">${name}</span>`,
    };
}
export function getTeam(team: Teams): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('Team', TeamQuery[team]),
    });
}

export function getEnvironment(env: EnvironmentKeys): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('Environment', EnvironmentMap[env]),
    });
}
export function getDomain(compName: Components): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('Component', ComponentAbbreviations[compName]),
    });
}
export function getE2eProject(hasE2E: boolean): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRowCheckbox(hasE2E, 'E2E Project'),
    });
}
export function getLeveragesNonCritical(deps: string[]): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRowCheckbox(
            !!deps?.length,
            'Leverages Non Critical Dependencies',
        ),
        tooltipHeader: !!deps?.length ? 'Non Critical Dependencies' : '',
        tooltipBody: !!deps?.length
            ? `<ul>
            ${deps.map((dep) => `<li>${startCase(dep)}</li>`).join('')}
        </ul>
        `
            : '',
    });
}
export function getComponetized(componentized: boolean): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRowCheckbox(componentized, 'Componentized'),
    });
}
export function getStability(p0: number): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('Stability', '--'),
    });
}
export function getAbstraction(p0: number): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('Abstraction', '--'),
    });
}
export function getModularity(p0: number): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('Modularity', '--'),
    });
}
export function getTestCoverage(p0: number): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('Test Coverage', '--%'),
    });
}
export function getUiUxCoverage(p0: number): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('UI/UX Coverage', '--%'),
    });
}
export function getE2eCoverage(p0: number): InfoLabel {
    return InfoLabelSchema.parse({
        label: _getInfoCardRow('E2E Coverage', '--%'),
    });
}

function _getInfoCardRow(title: string, label: string): string {
    return `<span class="font-body-2-strong">${title}:</span>
    <span class="font-body-2">${label}</span>`;
}
function _getInfoCardRowCheckbox(checked: boolean, label: string): string {
    return `<div class="grid grid-cols-[24px_1fr] gap-x-2 font-body-2">${
        checked ? checkBoxLabelChecked(label) : checkBoxLabelUnchecked(label)
    }</div>`;
}
